import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatusPill } from '@/components/AdminUsers/UserStatusPill';
import { UserActionButtons } from '@/components/AdminUsers/UserActionButtons';
import { formatUserDate, formatUserTime, getUserStatus } from '@/utils/adminUserStatus';
import type { AdminUser, AdminUserActionHandlers } from '@/types/adminUsers';
import styles from './styles.module.scss';

export interface UsersTableProps extends AdminUserActionHandlers {
  users: AdminUser[];
}

/** Desktop (≥ lg) admin users table. */
export const UsersTable: React.FC<UsersTableProps> = ({ users, ...actionHandlers }) => {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.language;

  return (
    <div className={styles.wrapper}>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead className={styles.head}>
            <tr>
              <th className={styles.th}>{t('admin:usersColUser')}</th>
              <th className={styles.th}>{t('admin:usersColStatus')}</th>
              <th className={styles.th}>{t('admin:usersColCreated')}</th>
              <th className={styles.th}>{t('admin:usersColLastLogin')}</th>
              <th className={`${styles.th} ${styles.right}`}>{t('admin:usersColActions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={styles.tr} data-testid={`admin-user-${u.id}`}>
                <td className={styles.td}>
                  <div className={styles.userCell}>
                    <div className={styles.avatar} aria-hidden="true">
                      {u.first_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>
                        {u.first_name} {u.last_name || ''}
                      </div>
                      <div className={styles.userEmail}>
                        {u.email ||
                          (u.username
                            ? `@${u.username}`
                            : t('admin:userTelegramId', { id: u.telegram_id }))}
                      </div>
                    </div>
                  </div>
                </td>
                <td className={styles.td}>
                  <UserStatusPill status={getUserStatus(u)} />
                  {u.blocked_reason && (
                    <div className={styles.blockedReason}>
                      {t('admin:userBlockedReason', { reason: u.blocked_reason })}
                    </div>
                  )}
                </td>
                <td className={`${styles.td} ${styles.date}`}>{formatUserDate(u.created_at, locale)}</td>
                <td className={`${styles.td} ${styles.date}`}>
                  {u.last_login ? (
                    <span className={styles.dateTime}>
                      <span>{formatUserDate(u.last_login, locale)}</span>
                      <span>{formatUserTime(u.last_login, locale)}</span>
                    </span>
                  ) : (
                    t('common:never')
                  )}
                </td>
                <td className={styles.td}>
                  <UserActionButtons user={u} {...actionHandlers} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTable;
